import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../context/auth-provider';
import { StateSwitch } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { toast } from '../../../../utils/toast';
import { formatDate } from '../../../../function-utils/locale';
import { StaysSectionTabs } from '../stays-section';
import { Quiet, StepTitle, Why } from '../setup/setup-wizard.styled';
import { CardList, CardMeta, CardTitle, Page, PageHead, PropertyCard } from '../properties/properties.styled';
import {
  AccountApiError,
  AccountExport,
  ClosePreview,
  closeAccount,
  createExport,
  exportLink,
  listExports,
  previewClose,
} from '../../../../api/account-client';

/**
 * STAYS → YOUR DATA — /dashboard/stays/your-data (Independent Stays IS8.2/IS8.3).
 *
 * The owner of a Stays-only account downloads everything in it and closes it
 * (IS-Q14, IS8-D3). Anyone else is told who can (the server refuses them too).
 *
 *  - Download everything: one zip, made now, kept 24 hours, opened through a
 *    5-minute link. No ID photos (never kept), no check-in links, no passwords.
 *  - Close: refused while stays are still to come (IS8-D6); otherwise the
 *    owner types the account name and the account is frozen for the grace
 *    period — everyone is signed out, guests' links stop, the subscription
 *    ends at period end. The owner can sign in and reopen until the date; then
 *    the account and its data are removed.
 */

const bytes = (n: number | null) =>
  n === null ? '' : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

export const YourDataPage: React.FC = () => {
  const { user, logout } = useAuth();
  const isOwner = (user as { role?: string } | null | undefined)?.role === 'owner';
  const [view, setView] = useState<'loading' | 'ready' | 'error' | 'refused'>('loading');
  const [refusal, setRefusal] = useState('');
  const [exports, setExports] = useState<AccountExport[]>([]);
  const [making, setMaking] = useState(false);
  const [preview, setPreview] = useState<ClosePreview | null>(null);
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState('');

  const load = useCallback(() => {
    if (!isOwner) {
      setRefusal('Only the account owner can download the account or close it.');
      setView('refused');

      return;
    }
    Promise.all([listExports(), previewClose()])
      .then(([list, p]) => {
        setExports(list);
        setPreview(p);
        setView('ready');
      })
      .catch(e => {
        if (e instanceof AccountApiError && e.status === 403) {
          setRefusal(e.message);
          setView('refused');
        } else setView('error');
      });
  }, [isOwner]);
  useEffect(() => load(), [load]);

  const make = async () => {
    setMaking(true);
    try {
      await createExport();
      toast.success('Your download is ready.');
      setExports(await listExports());
    } catch (e) {
      toast.error(e instanceof AccountApiError ? e.message : 'The download could not be made. Please try again.');
    } finally {
      setMaking(false);
    }
  };

  const download = async (id: string) => {
    try {
      window.location.assign(await exportLink(id));
    } catch (e) {
      toast.error(e instanceof AccountApiError ? e.message : 'That download is not available.');
      setExports(await listExports().catch(() => exports));
    }
  };

  const close = async () => {
    if (!preview) return;
    if (typed.trim() !== preview.confirm_with.trim()) {
      setTypedError('Type the account name exactly as shown.');

      return;
    }
    try {
      const r = await closeAccount(typed.trim());
      toast.success(
        r.freeze_until
          ? `Your account is closed. You can reopen it until ${formatDate(r.freeze_until)}.`
          : 'Your account is closed.'
      );
      // Every session was signed out by the close.
      await logout(false, { sessionEnded: true });
    } catch (e) {
      const msg = e instanceof AccountApiError ? e.message : 'That did not go through. Nothing was changed.';
      setTypedError(msg);
    }
  };

  const ready = exports.filter(x => x.status === 'ready' && new Date(x.expires_at) > new Date());
  const blocked = preview ? preview.blockers.find(b => b.code === 'ACCOUNT_HAS_FUTURE_STAYS') : undefined;

  return (
    <Page data-your-data-page="">
      <StaysSectionTabs />
      <PageHead>
        <div>
          <StepTitle as="h2">Your data</StepTitle>
          <Why>Download everything in your account, or close it.</Why>
        </div>
      </PageHead>
      <StateSwitch state={view}>
        {view === 'loading' ? (
          <ListSkeleton rows={2} label="Your data" />
        ) : view === 'refused' ? (
          <Quiet data-your-data-refused="">{refusal}</Quiet>
        ) : view === 'error' || !preview ? (
          <LoadError message="Could not load your data page." onRetry={() => load()} />
        ) : (
          <>
            <section aria-labelledby="yd-download">
              <StepTitle as="h3" id="yd-download">
                Download everything
              </StepTitle>
              <Why>
                One file with your properties, listings, stays, guests&apos; check-in details, guides, house rules, messages,
                team and invoices — each as a spreadsheet and as a data file. ID photos are never kept, so none are in
                it; nor are check-in links or passwords. A download is kept for 24 hours.
              </Why>
              <AppButton onClick={make} loading={making} data-your-data-export="">
                Make a download
              </AppButton>
              {ready.length ? (
                <CardList>
                  {ready.map(x => (
                    <PropertyCard key={x.id}>
                      <CardTitle>Made {formatDate(x.created_at)}</CardTitle>
                      <CardMeta>
                        {x.files} files · {bytes(x.bytes)} · available until {formatDate(x.expires_at)}
                      </CardMeta>
                      <AppButton variant="secondary" size="compact" onClick={() => download(x.id)}>
                        Download
                      </AppButton>
                    </PropertyCard>
                  ))}
                </CardList>
              ) : null}
            </section>

            <section aria-labelledby="yd-close">
              <StepTitle as="h3" id="yd-close">
                Close your account
              </StepTitle>
              <Why>
                Everyone is signed out and your guests&apos; check-in links stop working.
                {preview.effects.subscription_ends_at_period_end
                  ? ' Your subscription ends at the end of the period you have paid for.'
                  : ''}{' '}
                For {preview.grace_days} days you can sign in and reopen it, and everything comes back. After{' '}
                {formatDate(preview.freeze_until)} the account and its data are removed for good. Make a download
                first if you want to keep anything.
              </Why>
              {blocked ? (
                <Quiet data-your-data-blocked="">
                  You still have {blocked.count} stay{blocked.count === 1 ? '' : 's'} to come. Cancel or finish{' '}
                  {blocked.count === 1 ? 'it' : 'them'} first: closing Lobbero does not cancel a booking on Airbnb,
                  Booking.com or anywhere else. <Link to={blocked.link}>See your stays</Link>
                </Quiet>
              ) : (
                <AppButton variant="destructive" onClick={() => setAsking(true)} data-your-data-close="">
                  Close my account
                </AppButton>
              )}
            </section>

            <ConfirmDialog
              open={asking}
              title="Close your account?"
              message={`Type the account name, ${preview.confirm_with}, to close it. You can reopen it until ${formatDate(preview.freeze_until)}.`}
              primaryLabel="Close my account"
              primaryVariant="destructive"
              onPrimary={close}
              onDismiss={() => {
                setAsking(false);
                setTyped('');
                setTypedError('');
              }}
            >
              <Field>
                <Label htmlFor="yd-confirm">Account name</Label>
                <Input
                  id="yd-confirm"
                  value={typed}
                  autoComplete="off"
                  onChange={e => {
                    setTyped(e.target.value);
                    setTypedError('');
                  }}
                  aria-invalid={!!typedError}
                />
                <FieldError message={typedError} />
                {typedError ? null : <FieldHint>{preview.confirm_with}</FieldHint>}
              </Field>
            </ConfirmDialog>
          </>
        )}
      </StateSwitch>
    </Page>
  );
};
